/*
 * features.ts — build-time feature gates for the AquaStar customer SPA.
 *
 * WHY THIS EXISTS (2026-08-08)
 *   The Splash assistant FAB rendered on every authed page of prod, but prod
 *   has no AI configured at all — zero rows in ai_chat_providers, zero
 *   ai_hosts. Tapping it produced a raw gateway error. An affordance that is
 *   always visible and never works is worse than no affordance.
 *
 * WHY BUILD-TIME AND NOT A CAPABILITY FETCH
 *   Nexus HAS a proper answer to this — the instance/app/user capability tiers
 *   (NEXUS-21), where core/ai_chat/router.ts asserts the 'ai' capability and
 *   the client reads its own entitlements and hides what it cannot use. But
 *   the AquaStar prod gateway is pinned to 0f427ace, which predates all of it:
 *   no core/capabilities, no migrations 039-041, no capability tables in the
 *   prod database. There is nothing for a client to ask today.
 *
 *   So this is deliberately the dumb version, and deliberately temporary.
 *
 * REPLACING IT
 *   When the capability work is promoted to prod (NEXUS-21 P5 — client
 *   entry-point gating), delete this module and gate on the live capability
 *   instead. A build flag cannot be changed without a rebuild, which is the
 *   whole failing we are working around on iOS right now; do not let it
 *   calcify into the permanent answer for the web too.
 *
 * DEFAULTS
 *   Unset means ENABLED. dev and uat pass nothing and keep working exactly as
 *   before; only the prod launcher opts out. A default-off flag would have
 *   silently killed Splash on every developer machine.
 */

/** Read a Vite env var as a boolean. Unset → `fallback`. */
function envFlag(raw: string | undefined, fallback: boolean): boolean {
  if (raw === undefined || raw === '') return fallback;
  return raw !== '0' && raw.toLowerCase() !== 'false' && raw.toLowerCase() !== 'no';
}

/**
 * Is the Splash assistant available on this deployment?
 *
 * Set `VITE_SPLASH_ENABLED=0` at build time to hide the FAB entirely.
 * The prod launcher (run-deploy-aquastar-prod.command) does exactly that,
 * because prod has no AI provider configured.
 */
export const SPLASH_ENABLED = envFlag(
  import.meta.env.VITE_SPLASH_ENABLED as string | undefined,
  true,
);
